import { Backdrop, Box, CircularProgress, Divider, Typography } from "@mui/material";
import { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { Select, Badge } from 'antd';
import Table from '../../TableResizable';
import { Crypter } from "../../../Functions/Security/Crypter";
import axios from "axios";
import Data from '../../../Data/Data.json';
import { useAuthHeader } from "../../../Services/Auth/sessionAuth";
import ParamsPicklist from "../../../Types/ParamsPicklist";
import formatNumber from "../../../Functions/Traitement/FormatNumber";
import ReformatDate from "../../../Functions/Traitement/ReformatDate";

// ─── Types internes ───────────────────────────────────────────────────────────
interface DossierRow {
    key: number;
    NumeroFinancement: string;
    CodeProduit: string;
    Numcli: string;
    Nomcli: string;
    DateDemande: string;
    DateVal: string;
    CODGEA: string;
    MontantValide: string;
    TotalDecaisse: string;
    ResteADecaisser: string;
}

interface EcheanceRow {
    key: number;
    NumeroFinancement: string;
    NumeroEcheance: string;
    DateEcheance: string;
    MontantCapital: string;
    MontantPaye: string;
    SoldeEcheance: string;
    DatePaiement: string;
    Statut: string;
}

interface Props {
    form?: any;
    dataFindQard: any[];
    dataFindSalam?: any[];
    dataFindMourabaha?: any[];
}

/** Configuration d'un produit de financement */
interface ProduitConfig {
    key: string;
    label: string;
    rows: DossierRow[];
    labelCode: string;
    withAmort: boolean;
    amortTable?: string;
    color: string;
}

// ─── Colonnes communes ───────────────────────────────────────────────────────
const colsDossier = (label: string): ColumnsType<DossierRow> => [
    { title: 'N° Financement', dataIndex: 'NumeroFinancement', width: 155, ellipsis: true },
    { title: label,            dataIndex: 'CodeProduit',       width: 100, ellipsis: true },
    { title: 'Client',         dataIndex: 'Numcli',            width: 90,  ellipsis: true },
    { title: 'Nom client',     dataIndex: 'Nomcli',            width: 130, ellipsis: true },
    { title: 'Date demande',   dataIndex: 'DateDemande',       width: 115, ellipsis: true },
    { title: 'Date validation',dataIndex: 'DateVal',           width: 115, ellipsis: true },
    { title: 'Gestionnaire',   dataIndex: 'CODGEA',            width: 100, ellipsis: true },
    {
        title: 'Montant validé', dataIndex: 'MontantValide',
        width: 120, align: 'right', ellipsis: true,
        render: v => <span style={{ fontWeight: 600, color: '#04446e' }}>{v}</span>
    },
    {
        title: 'Décaissé', dataIndex: 'TotalDecaisse',
        width: 110, align: 'right', ellipsis: true,
        render: v => <span style={{ color: '#c0392b' }}>{v}</span>
    },
    {
        title: 'Reste à décaisser', dataIndex: 'ResteADecaisser',
        width: 140, align: 'right', ellipsis: true,
        render: v => <span style={{ color: '#00875A', fontWeight: 600 }}>{v}</span>
    },
];

// ─── Colonnes tableau d'amortissement ───────────────────────────────────────
const colsAmort: ColumnsType<EcheanceRow> = [
    { title: 'N° Éch.',        dataIndex: 'NumeroEcheance', width: 70,  align: 'center' },
    { title: 'Date échéance',  dataIndex: 'DateEcheance',   width: 115, ellipsis: true  },
    { title: 'Amortissement',  dataIndex: 'MontantCapital', width: 120, align: 'right',
      render: v => <span style={{ color: '#2A6596' }}>{v}</span> },
    { title: 'Montant payé',   dataIndex: 'MontantPaye',    width: 115, align: 'right',
      render: v => <span style={{ color: '#00875A' }}>{v}</span> },
    { title: 'Solde échéance', dataIndex: 'SoldeEcheance',  width: 115, align: 'right',
      render: v => <span style={{ color: '#c0392b' }}>{v}</span> },
    { title: 'Date paiement',  dataIndex: 'DatePaiement',   width: 115, ellipsis: true  },
    {
        title: 'Statut', dataIndex: 'Statut', width: 90, align: 'center',
        render: (v: string) => (
            <Badge
                color={v === 'Payé' ? 'green' : v === 'Partiel' ? 'orange' : 'red'}
                text={v || '—'}
            />
        ),
    },
];

// ─── Helpers ────────────────────────────────────────────────────────────────
const mapDossier = (data: any, indice: number, produitField: string): DossierRow => ({
    key: indice,
    NumeroFinancement: data.NumeroFinancement ?? '',
    CodeProduit:       data[produitField] ?? data.CodeQard ?? data.CodeSalam ?? data.CodeMourabaha ?? '',
    Numcli:            data.Numcli  ?? '',
    Nomcli:            data.Nomcli  ?? '',
    DateDemande:       data.DateDemande ?? '',
    DateVal:           data.DateVal ?? '',
    CODGEA:            data.CODGEA ?? '',
    MontantValide:     data.MontantValide ?? data.PrixVenteValide ?? '',
    TotalDecaisse:     data.TotalDecaisse ?? '',
    ResteADecaisser:   data.ResteADecaisser ?? '',
});

const mapEcheance = (d: any, i: number): EcheanceRow => ({
    key: i,
    NumeroFinancement: d.NumeroFinancement ?? '',
    NumeroEcheance:    String(d.NumeroEcheance ?? ''),
    DateEcheance:      d.DateEcheance
        ? new Date(d.DateEcheance.substring(0, 10)).toLocaleDateString('fr')
        : '',
    MontantCapital:    formatNumber(d.MontantCapital ?? d.MontantAmortissement ?? 0),
    MontantPaye:       formatNumber(d.MontantPaye ?? 0),
    SoldeEcheance:     formatNumber(d.SoldeEcheance ?? d.Solde ?? 0),
    DatePaiement:      d.DatePaiement ? ReformatDate(d.DatePaiement) : '',
    Statut:            d.Statut ?? '',
});

// ─── Sous-composant : panneau d'un produit ────────────────────────────────────
interface PanelProps {
    dossiers: DossierRow[];
    labelCode: string;
    withAmort: boolean;
    amortTable?: string;
    authHeader: () => string;
}

const ProduitPanel = ({
    dossiers,
    labelCode,
    withAmort,
    amortTable,
    authHeader,
}: PanelProps) => {
    const [selectedKey, setSelectedKey]   = useState<React.Key[]>([]);
    const [amortData,   setAmortData]     = useState<EcheanceRow[]>([]);
    const [loadingAmort, setLoadingAmort] = useState(false);

    const fetchAmort = (numDossier: string) => {
        if (!amortTable) return;

        const searchTabAmort: ParamsPicklist = {
            CodeAgence: numDossier,
            Table: amortTable,
            Colonne1: "", Colonne2: "",
            Colagence: "NumeroFinancement",
            Choix: 32,
        };
        setLoadingAmort(true);
        axios({
            method: 'get', maxBodyLength: Infinity,
            url: Data.ApiUrl.AnySearch,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authHeader(),
                'CypherSearch': Crypter.encrypt(JSON.stringify(searchTabAmort)),
            },
        }).then(response => {
            if (response.data) {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value;
                if (res.StatutCode === 200 && res.Result?.Table?.length > 0) {
                    setAmortData(res.Result.Table.map(mapEcheance));
                } else {
                    setAmortData([]);
                }
            }
        }).catch(console.error)
          .finally(() => setLoadingAmort(false));
    };

    const onSelect = (newKey: any) => {
        setSelectedKey(newKey);
        const row = dossiers[newKey];
        if (row) fetchAmort(row.NumeroFinancement);
    };

    const selected = selectedKey.length > 0 ? dossiers[selectedKey[0] as number] : null;

    return (
        <Box sx={{ pt: 1 }}>
            {/* Tableau dossiers */}
            <Box
                className="bordure1"
                sx={{ mb: 2, borderRadius: '8px', overflow: 'hidden' }}
            >
                <Box sx={{
                    px: 2, py: 0.8,
                    background: 'linear-gradient(90deg, #04446e 0%, #2A6596 100%)',
                    display: 'flex', alignItems: 'center', gap: 1,
                }}>
                    <span style={{ color: '#B0D2F7', fontWeight: 700, fontSize: 12, letterSpacing: 0.5 }}>
                        DOSSIERS DE FINANCEMENT
                    </span>
                    <Badge
                        count={dossiers.length}
                        style={{ backgroundColor: '#B0D2F7', color: '#04446e', fontWeight: 700, fontSize: 11 }}
                    />
                </Box>
                <Table
                    size="small"
                    pagination={false}
                    rowSelection={{
                        selectedRowKeys: selectedKey,
                        onChange: onSelect,
                        type: 'radio',
                    }}
                    scroll={{ y: 160, x: 1100 }}
                    columns={colsDossier(labelCode)}
                    dataSource={dossiers}
                    locale={{ emptyText: 'Aucun dossier de financement.' }}
                    rowClassName={(_, i) => i % 2 === 0 ? 'row-even' : ''}
                />
            </Box>

            {/* Résumé du dossier sélectionné */}
            {selected && (
                <Box sx={{
                    display: 'flex', flexWrap: 'wrap', gap: 2, px: 2, py: 1.2, mb: 2,
                    borderRadius: '8px', bgcolor: '#f0f6ff',
                    border: '1px solid #b3d4f0',
                    fontSize: 12, color: '#2d4a5e',
                }}>
                    <span><b>Dossier :</b> {selected.NumeroFinancement}</span>
                    <Divider orientation="vertical" flexItem />
                    <span><b>Client :</b> {selected.Nomcli}</span>
                    <Divider orientation="vertical" flexItem />
                    <span><b>Validé :</b> <span style={{ color: '#04446e', fontWeight: 700 }}>{selected.MontantValide}</span></span>
                    <Divider orientation="vertical" flexItem />
                    <span><b>Décaissé :</b> <span style={{ color: '#c0392b', fontWeight: 700 }}>{selected.TotalDecaisse || '—'}</span></span>
                    <Divider orientation="vertical" flexItem />
                    <span><b>Reste :</b> <span style={{ color: '#00875A', fontWeight: 700 }}>{selected.ResteADecaisser || '—'}</span></span>
                </Box>
            )}

            {/* Tableau amortissement */}
            {withAmort && (
                <Box
                    className="bordure1"
                    sx={{ borderRadius: '8px', overflow: 'hidden', position: 'relative' }}
                >
                    <Box sx={{
                        px: 2, py: 0.8,
                        background: 'linear-gradient(90deg, #00875A 0%, #00a36e 100%)',
                        display: 'flex', alignItems: 'center', gap: 1,
                    }}>
                        <span style={{ color: '#fff', fontWeight: 700, fontSize: 12, letterSpacing: 0.5 }}>
                            TABLEAU D'AMORTISSEMENT
                        </span>
                        {selectedKey.length === 0 && (
                            <span style={{ color: 'rgba(255,255,255,.7)', fontSize: 11, fontStyle: 'italic' }}>
                                — Sélectionnez un dossier
                            </span>
                        )}
                        {amortData.length > 0 && (
                            <Badge
                                count={amortData.length}
                                style={{ backgroundColor: '#fff', color: '#00875A', fontWeight: 700, fontSize: 11 }}
                            />
                        )}
                    </Box>
                    <Table
                        size="small"
                        pagination={false}
                        scroll={{ y: 170, x: 700 }}
                        columns={colsAmort}
                        dataSource={amortData}
                        locale={{
                            emptyText: selectedKey.length === 0
                                ? 'Sélectionnez un dossier pour afficher le tableau d\'amortissement.'
                                : 'Aucune échéance trouvée pour ce dossier.'
                        }}
                    />
                    <Backdrop
                        sx={{ color: '#fff', zIndex: t => t.zIndex.drawer + 1, position: 'absolute', borderRadius: '8px' }}
                        open={loadingAmort}
                    >
                        <CircularProgress color="inherit" size={32} />
                    </Backdrop>
                </Box>
            )}

            {/* Message pour les produits sans amortissement (ex: Salam) */}
            {!withAmort && selectedKey.length > 0 && (
                <Box sx={{
                    px: 2, py: 2, borderRadius: '8px',
                    border: '1px dashed #b3d4f0', bgcolor: '#f7fafd',
                    textAlign: 'center', fontSize: 12, color: '#90a4b7', fontStyle: 'italic',
                }}>
                    Le tableau d'amortissement n'est pas disponible pour ce type de financement.
                </Box>
            )}
        </Box>
    );
};

// ─── Composant principal Tab2 ─────────────────────────────────────────────────
const Tab2 = ({ dataFindQard, dataFindSalam = [], dataFindMourabaha = [] }: Props) => {
    const authHeader = useAuthHeader();

    // ── Configuration des produits ────────────────────────────────────────
    // Ajouter un nouveau produit = ajouter une entrée dans ce tableau.
    const produits: ProduitConfig[] = [
        {
            key: 'qard',
            label: 'Qard',
            rows: dataFindQard.map((d, i) => mapDossier(d, i, 'CodeQard')),
            labelCode: 'Code Qard',
            withAmort: true,
            amortTable: 'FinQard_Echeance',
            color: '#2A6596',
        },
        {
            key: 'salam',
            label: 'Salam',
            rows: dataFindSalam.map((d, i) => mapDossier(d, i, 'CodeSalam')),
            labelCode: 'Code Salam',
            withAmort: false,
            color: '#00875A',
        },
        {
            key: 'mourabaha',
            label: 'Mourabaha',
            rows: dataFindMourabaha.map((d, i) => mapDossier(d, i, 'CodeMourabaha')),
            labelCode: 'Code Mourabaha',
            withAmort: true,
            amortTable: 'FinMourabaha_Echeance',
            color: '#b45309',
        },
    ];

    // On ne propose que les produits qui ont des dossiers
    const availableProduits = produits.filter(p => p.rows.length > 0);

    const [selectedProduit, setSelectedProduit] = useState<string>('');

    // Auto-sélection du premier produit dispo / réinitialisation si plus rien
    useEffect(() => {
        if (availableProduits.length === 0) {
            if (selectedProduit !== '') setSelectedProduit('');
            return;
        }
        if (!availableProduits.find(p => p.key === selectedProduit)) {
            setSelectedProduit(availableProduits[0].key);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataFindQard.length, dataFindSalam.length, dataFindMourabaha.length, selectedProduit]);

    const currentProduit = availableProduits.find(p => p.key === selectedProduit);

    return (
        <div className="tabItem">
            <Box width="85%" position="absolute">

                {/* ── Sélecteur de produit ──────────────────────────────── */}
                <Box
                    sx={{
                        mb: 1.5, p: 1.5, borderRadius: 2,
                        bgcolor: '#fff',
                        border: '1px solid #e3e8ee',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                        boxShadow: '0 1px 3px rgba(0,0,0,.04)',
                    }}
                >
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#04446e' }}>
                        Produit de financement :
                    </Typography>

                    <Select
                        value={selectedProduit || undefined}
                        onChange={(v) => setSelectedProduit(v)}
                        style={{ width: 320 }}
                        size="large"
                        placeholder="Choisir un produit..."
                        disabled={availableProduits.length === 0}
                        options={availableProduits.map(p => ({
                            value: p.key,
                            label: (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span
                                        style={{
                                            display: 'inline-block',
                                            width: 8, height: 8, borderRadius: '50%',
                                            backgroundColor: p.color,
                                        }}
                                    />
                                    <span style={{ fontWeight: 600 }}>{p.label}</span>
                                    <span style={{ color: '#8c8c8c', fontSize: 12 }}>
                                        ({p.rows.length})
                                    </span>
                                </span>
                            ),
                        }))}
                    />

                    {currentProduit && (
                        <Typography sx={{ fontSize: 12, color: '#666', ml: 'auto' }}>
                            {currentProduit.rows.length} dossier(s) trouvé(s)
                        </Typography>
                    )}
                </Box>

                {/* ── Panneau du produit sélectionné ───────────────────── */}
                {currentProduit ? (
                    <ProduitPanel
                        key={currentProduit.key}   // ← force le remount au changement de produit
                        dossiers={currentProduit.rows}
                        labelCode={currentProduit.labelCode}
                        withAmort={currentProduit.withAmort}
                        amortTable={currentProduit.amortTable}
                        authHeader={authHeader}
                    />
                ) : (
                    <Box sx={{
                        p: 4, textAlign: 'center', borderRadius: 2,
                        border: '1px dashed #cfd8dc', bgcolor: '#fafbfc',
                        color: '#90a4b7', fontStyle: 'italic', fontSize: 13,
                    }}>
                        Aucun produit de financement disponible pour ce client.
                    </Box>
                )}
            </Box>
        </div>
    );
};

export default Tab2;